/**
 * InvestorSidePanel — slide-in-from-right panel surfaced when an admin
 * clicks an investor row on /admin/market/investors.
 *
 * Sections:
 *   1. Header        — name, firm, email, status pill, registration date
 *   2. Profile       — collapsible (open by default), 2-col grid of every
 *                      investor registration field
 *   3. Documents     — collapsible (open by default), upload dropdown,
 *                      send-NDA button, per-row View / Delete, status
 *                      badges (signed / pending / uploaded / voided)
 *   4. Activity      — collapsible (closed by default), simple vertical
 *                      timeline of registration / NDA / LOI / DD / bids
 *
 * Backed by /api/admin/market/investors/:id/documents (extended
 * market_documents table). Docs are fetched after the panel opens to
 * keep the open animation snappy; a skeleton renders while loading.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../utils/icons';
import { useIsMobile } from '../../utils/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TYPE_LABELS = {
  fund: 'Credit Fund',
  family_office: 'Family Office',
  reit: 'REIT',
  trade_desk: 'Trade Desk',
  individual: 'Individual',
  other: 'Other',
};

const DOC_TYPE_LABELS = {
  nda: 'NDA',
  loi: 'LOI',
  subscription_agreement: 'Subscription',
  accreditation: 'Accreditation',
  other: 'Other',
};

const DOC_TYPE_OPTIONS = [
  { value: 'nda',                    label: 'NDA' },
  { value: 'loi',                    label: 'LOI' },
  { value: 'subscription_agreement', label: 'Subscription Agreement' },
  { value: 'accreditation',          label: 'Accreditation Letter' },
  { value: 'other',                  label: 'Other' },
];

// boldsign_status string → pill colour. 'completed' / 'signed' both
// show as green; sent / viewed / pending_manual show yellow; declined /
// expired / revoked show red; bare 'uploaded' (admin-uploaded with no
// envelope) shows blue.
const STATUS_BADGE = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'signed')                              return { bg: '#f0fde4', color: '#111111', label: 'Signed' };
  if (s === 'sent' || s === 'viewed' || s === 'pending_manual' || s === 'reminder')
                                                                         return { bg: '#fffde4', color: '#7a5200', label: 'Pending Signature' };
  if (s === 'declined' || s === 'expired' || s === 'revoked' || s === 'voided')
                                                                         return { bg: '#f3f4f6', color: '#6b7280', label: s.charAt(0).toUpperCase() + s.slice(1) };
  if (s === 'uploaded')                                                  return { bg: '#eff4ff', color: '#3a6ad9', label: 'Uploaded' };
  return { bg: '#f3f4f6', color: '#6b7280', label: status || '—' };
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const Section = ({ title, right, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.06 }}>{title}</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{open ? '▾' : '▸'}</span>
        </div>
        {right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
      </div>
      {open && <div style={{ padding: '0 22px 18px' }}>{children}</div>}
    </div>
  );
};

export default function InvestorSidePanel({ investor, onClose, onChange }) {
  const open = !!investor;
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [busyDocId, setBusyDocId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sendingNda, setSendingNda] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState('');
  // Prior Trades — bid history loaded from
  // /api/admin/market/investors/:id/trades. Side-loaded so the panel
  // open animation isn't blocked on it; skeleton renders meanwhile.
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchDocs = useCallback(async (id) => {
    setDocsLoading(true);
    setDocsError('');
    try {
      const r = await axios.get(`${API}/api/admin/market/investors/${id}/documents`, { headers });
      setDocs(r.data.documents || []);
    } catch (err) {
      setDocsError(err.response?.data?.error || 'Failed to load documents.');
    } finally {
      setDocsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTrades = useCallback(async (id) => {
    setTradesLoading(true);
    setTradesError('');
    try {
      const r = await axios.get(`${API}/api/admin/market/investors/${id}/trades`, { headers });
      setTrades(r.data.trades || []);
    } catch (err) {
      setTradesError(err.response?.data?.error || 'Failed to load trade history.');
    } finally {
      setTradesLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (investor?.id) {
      setDocs([]);
      setTrades([]);
      fetchDocs(investor.id);
      fetchTrades(investor.id);
    }
  }, [investor?.id, fetchDocs, fetchTrades]);

  const handleUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !uploadType) return;
    if (file.size > 10 * 1024 * 1024) { alert('PDF too large (10MB max).'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = String(reader.result || '').split(',')[1] || '';
        await axios.post(
          `${API}/api/admin/market/investors/${investor.id}/documents`,
          { document_type: uploadType, document_name: file.name, file_base64: base64, mime_type: file.type || 'application/pdf' },
          { headers }
        );
        setToast('Document uploaded.');
        setTimeout(() => setToast(''), 3000);
        fetchDocs(investor.id);
        if (onChange) onChange();
      } catch (err) {
        alert('Upload failed: ' + (err.response?.data?.error || err.message));
      } finally {
        setUploading(false);
        setUploadOpen(false);
        setUploadType(null);
        e.target.value = '';
      }
    };
    reader.onerror = () => { alert('Could not read file.'); setUploading(false); };
    reader.readAsDataURL(file);
  };

  const handleView = async (docId) => {
    try {
      const r = await axios.get(
        `${API}/api/admin/market/investors/${investor.id}/documents/${docId}/download`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('Unable to open: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (docId) => {
    setBusyDocId(docId);
    try {
      await axios.delete(`${API}/api/admin/market/investors/${investor.id}/documents/${docId}`, { headers });
      fetchDocs(investor.id);
      if (onChange) onChange();
      setConfirmDelete(null);
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyDocId(null);
    }
  };

  const handleSendNda = async () => {
    setSendingNda(true);
    try {
      await axios.post(`${API}/api/admin/market/investors/${investor.id}/send-nda`, {}, { headers });
      // Not reached today: the endpoint returns 501 (NDA send is not implemented).
      setToast('NDA send requested.');
      setTimeout(() => setToast(''), 4000);
      fetchDocs(investor.id);
      if (onChange) onChange();
    } catch (err) {
      alert('NDA not sent: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingNda(false);
    }
  };

  if (!open) return null;
  const fullName = `${investor.first_name || ''} ${investor.last_name || ''}`.trim();

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)',
          zIndex: 90, animation: 'uw-fade-in 0.18s ease-out',
        }}
      />
      {/* Slide panel — top:64 clears the fixed AdminNavbar (zIndex 1000) so
          the panel header isn't hidden behind it. */}
      <div style={{
        position: 'fixed', top: isMobile ? 56 : 64, right: 0, bottom: 0,
        width: isMobile ? '100%' : '480px', maxWidth: '100vw',
        backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 1100, display: 'flex', flexDirection: 'column',
        transform: 'translateX(0)',
        animation: 'uw-slide-in 0.22s ease-out',
      }}>
        <style>{`
          @keyframes uw-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
          @keyframes uw-fade-in  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes uw-spin     { to { transform: rotate(360deg); } }
        `}</style>

        {/* Header */}
        <div style={{ padding: '20px 22px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fullName || investor.email || `Investor ${investor.id}`}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {investor.firm_name || '—'}{investor.email ? ` · ${investor.email}` : ''}
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                Registered: {fmtDate(investor.created_at)}
              </div>
            </div>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280', padding: 4 }}>×</button>
          </div>
          <div style={{ marginTop: 10 }}>
            <span style={{
              display: 'inline-block', padding: '3px 10px', borderRadius: 9999,
              backgroundColor: investor.status === 'approved' ? '#f0fde4' : investor.status === 'rejected' ? '#ffe6e9' : investor.status === 'pending' ? '#fffde4' : '#f3f4f6',
              color: investor.status === 'rejected' ? '#ff082f' : '#111111',
              fontSize: 11, fontWeight: 700, textTransform: 'capitalize',
            }}>{investor.status || '—'}</span>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ margin: '12px 22px 0', background: '#eff4ff', border: '1px solid #bfcff8', color: '#3a6ad9', borderRadius: 8, padding: '10px 12px', fontSize: 12, fontWeight: 600 }}>
            {toast}
          </div>
        )}

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          <Section title="Profile" defaultOpen>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 14px', fontSize: 12 }}>
              <ProfileField label="Full Name"     value={fullName || '—'} />
              <ProfileField label="Company"       value={investor.firm_name || '—'} />
              <ProfileField label="Email"         value={investor.email || '—'} />
              <ProfileField label="Phone"         value={investor.phone || '—'} />
              <ProfileField label="Investor Type" value={TYPE_LABELS[investor.investor_type] || investor.investor_type || '—'} />
              <ProfileField label="Title"         value={investor.title || '—'} />
              <ProfileField label="AUM"           value={investor.aum_range || '—'} />
              <ProfileField label="Investment Focus" value={investor.investment_focus || '—'} />
              <ProfileField label="NDA"           value={investor.nda_signed ? `Signed ${fmtDate(investor.nda_signed_at)}` : 'Not signed'} />
              <ProfileField label="Registered"    value={fmtDate(investor.created_at)} />
              <ProfileField label="Last Login"    value={fmtDate(investor.last_login_at)} />
              {investor.status === 'rejected' && (
                <ProfileField label="Rejection Reason" value={investor.rejection_reason || '—'} />
              )}
            </div>
          </Section>

          <Section
            title="Documents"
            defaultOpen
            right={
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setUploadOpen((v) => !v)}
                  style={{ padding: '5px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                  + Upload Document ▾
                </button>
                {uploadOpen && (
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200, zIndex: 5 }}>
                    {uploading ? (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, border: '2px solid #e5e7eb', borderTopColor: '#3a6ad9', borderRadius: '50%', display: 'inline-block', animation: 'uw-spin 0.6s linear infinite' }} />
                        Uploading…
                      </div>
                    ) : (
                      DOC_TYPE_OPTIONS.map((opt) => (
                        <label key={opt.value}
                          style={{ display: 'block', padding: '8px 12px', fontSize: 12, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #f9fafb' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          {opt.label}
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={(e) => { setUploadType(opt.value); handleUpload(e); }}
                            style={{ display: 'none' }}
                          />
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            }
          >
            {docsLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[0, 1].map((i) => (
                  <div key={i} style={{ height: 56, background: '#f3f4f6', borderRadius: 8, animation: 'uw-fade-in 0.2s ease-out' }} />
                ))}
              </div>
            )}
            {!docsLoading && docsError && (
              <div style={{ background: '#ffe6e9', border: '1px solid #ffd6db', color: '#ff082f', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
                {docsError}{' '}
                <button onClick={() => fetchDocs(investor.id)} style={{ marginLeft: 8, background: 'none', border: '1px solid #ffd6db', color: '#ff082f', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Retry</button>
              </div>
            )}
            {!docsLoading && !docsError && docs.length === 0 && (
              <div style={{ textAlign: 'center', padding: 18, color: '#9ca3af', fontSize: 12 }}>
                <div style={{ marginBottom: 12 }}>No documents on file.</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button onClick={handleSendNda} disabled={sendingNda}
                    style={{ padding: '6px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, cursor: sendingNda ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, opacity: sendingNda ? 0.5 : 1 }}>
                    {sendingNda ? 'Sending…' : 'Send NDA for Signature'}
                  </button>
                  <button onClick={() => setUploadOpen(true)}
                    style={{ padding: '6px 12px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfcff8', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    Upload Document
                  </button>
                </div>
              </div>
            )}
            {!docsLoading && !docsError && docs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {docs.map((d) => {
                  const badge = STATUS_BADGE(d.boldsign_status);
                  const canView = !!d.has_file;
                  return (
                    <div key={d.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Icon name="document" size={14} color="#6b7280" />
                            {d.document_name || d.original_name || `${DOC_TYPE_LABELS[d.document_type] || d.document_type}.pdf`}
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                            <span style={{ background: '#eff4ff', color: '#3a6ad9', padding: '1px 8px', borderRadius: 9999, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>
                              {DOC_TYPE_LABELS[d.document_type] || d.document_type}
                            </span>
                            <span>{d.is_clickthrough ? 'Click-through' : `Uploaded ${fmtDate(d.upload_date)}`}</span>
                            <span>· Signed {d.signed_at ? fmtDate(d.signed_at) : 'Pending'}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 700, background: badge.bg, color: badge.color }}>
                            {badge.label}
                          </span>
                          <button
                            onClick={() => canView && handleView(d.id)}
                            disabled={!canView}
                            title={canView ? 'Open in a new tab' : d.is_clickthrough ? 'Click-through signature — no PDF generated' : 'No file payload (envelope-only record)'}
                            style={{ padding: '4px 10px', background: canView ? '#3a6ad9' : '#e5e7eb', color: canView ? '#fff' : '#9ca3af', border: 'none', borderRadius: 5, cursor: canView ? 'pointer' : 'not-allowed', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Icon name="download" size={11} color={canView ? '#fff' : '#9ca3af'} />
                            View
                          </button>
                          {!d.is_clickthrough && (
                            <button
                              onClick={() => setConfirmDelete(d.id)}
                              disabled={busyDocId === d.id}
                              title="Delete"
                              aria-label="Delete document"
                              style={{ padding: '4px 8px', background: 'none', border: '1px solid #e5e7eb', color: '#6b7280', borderRadius: 5, cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center' }}>
                              <Icon name="trash" size={13} color="#6b7280" />
                            </button>
                          )}
                        </div>
                      </div>
                      {confirmDelete === d.id && (
                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#fff5f6', border: '1px solid #ffd6db', borderRadius: 6, fontSize: 12, color: '#374151' }}>
                          Delete this document? This cannot be undone.
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                            <button onClick={() => setConfirmDelete(null)}
                              style={{ padding: '4px 10px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Cancel</button>
                            <button onClick={() => handleDelete(d.id)} disabled={busyDocId === d.id}
                              style={{ padding: '4px 10px', background: '#ff082f', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                              {busyDocId === d.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!docsLoading && !docsError && docs.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleSendNda} disabled={sendingNda}
                  style={{ padding: '6px 12px', background: 'none', color: '#3a6ad9', border: '1px solid #bfcff8', borderRadius: 6, cursor: sendingNda ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700, opacity: sendingNda ? 0.5 : 1 }}>
                  {sendingNda ? 'Sending NDA…' : '+ Send NDA for Signature'}
                </button>
              </div>
            )}
          </Section>

          <Section title="Prior Trades" defaultOpen>
            <TradesList
              trades={trades}
              loading={tradesLoading}
              error={tradesError}
              onRetry={() => fetchTrades(investor.id)}
              onViewPool={(poolId) => { onClose(); navigate(`/admin/market/pools/${poolId}`); }}
            />
          </Section>

          <Section title="Activity" defaultOpen={false}>
            <ActivityTimeline investor={investor} docs={docs} trades={trades} />
          </Section>

        </div>
      </div>
    </>
  );
}

function ProfileField({ label, value }) {
  return (
    <>
      <div style={{ color: '#6b7280', fontWeight: 600 }}>{label}</div>
      <div style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </>
  );
}

function ActivityTimeline({ investor, docs, trades }) {
  const events = [];
  if (investor?.created_at)        events.push({ at: investor.created_at,        label: 'Registered for DoorlyMarket' });
  if (investor?.approved_at)       events.push({ at: investor.approved_at,       label: 'Approved by admin' });
  if (investor?.rejected_at)       events.push({ at: investor.rejected_at,       label: 'Rejected by admin' });
  if (investor?.nda_signed_at)     events.push({ at: investor.nda_signed_at,     label: 'NDA signed (click-through)' });
  for (const d of docs || []) {
    if (d.upload_date) events.push({ at: d.upload_date, label: `${(DOC_TYPE_LABELS[d.document_type] || d.document_type)} ${d.boldsign_status === 'pending_manual' ? 'sent for signature' : 'uploaded'}` });
    if (d.signed_at)   events.push({ at: d.signed_at,   label: `${(DOC_TYPE_LABELS[d.document_type] || d.document_type)} signed` });
  }
  for (const t of trades || []) {
    if (t.bid_date)          events.push({ at: t.bid_date,          label: `Bid submitted on ${t.pool_number || t.pool_name || 'pool'} — $${Number(t.bid_amount || 0).toLocaleString()}` });
    if (t.dd_room_opened_at) events.push({ at: t.dd_room_opened_at, label: `DD room opened — ${t.pool_number || t.pool_name || 'pool'}` });
    if (t.loi_completed_at)  events.push({ at: t.loi_completed_at,  label: `LOI fully executed — ${t.pool_number || t.pool_name || 'pool'}` });
  }
  if (investor?.last_login_at)     events.push({ at: investor.last_login_at,     label: 'Last login' });
  events.sort((a, b) => new Date(b.at) - new Date(a.at));

  if (!events.length) return <div style={{ fontSize: 12, color: '#9ca3af', padding: 8 }}>No activity yet.</div>;

  return (
    <div style={{ position: 'relative', paddingLeft: 18 }}>
      <div style={{ position: 'absolute', top: 8, bottom: 8, left: 5, width: 1, background: '#e5e7eb' }} />
      {events.map((e, i) => (
        <div key={i} style={{ position: 'relative', paddingBottom: 12 }}>
          <div style={{ position: 'absolute', left: -16, top: 4, width: 8, height: 8, borderRadius: '50%', background: '#3a6ad9' }} />
          <div style={{ fontSize: 12, color: '#111827', fontWeight: 600 }}>{e.label}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            {new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Prior Trades helpers ────────────────────────────────────────────
// Status-badge mapping covers BOTH the spec vocabulary (loi_sent /
// accepted / closed / completed / declined) AND the codebase's actual
// market_bids.status values (loi_pending / dd_open / withdrawn /
// rejected). The frontend never has to know which one it sees.
const TRADE_STATUS_BADGE = (status) => {
  const s = String(status || '').toLowerCase();
  if (['accepted', 'closed', 'completed', 'funded'].includes(s))
    return { bg: '#f0fde4', color: '#111111', label: 'Closed' };
  if (s === 'loi_signed')
    return { bg: '#eff4ff', color: '#3a6ad9', label: 'LOI Signed' };
  if (s === 'dd_open')
    return { bg: '#eff4ff', color: '#3a6ad9', label: 'DD Room Open' };
  if (['loi_sent', 'loi_pending'].includes(s))
    return { bg: '#fffde4', color: '#7a5200', label: 'LOI Pending' };
  if (['submitted', 'pending'].includes(s))
    return { bg: '#f3f4f6', color: '#374151', label: 'Bid Submitted' };
  if (['rejected', 'declined'].includes(s))
    return { bg: '#ffe6e9', color: '#ff082f', label: 'Declined' };
  if (s === 'withdrawn')
    return { bg: '#f3f4f6', color: '#6b7280', label: 'Withdrawn' };
  return { bg: '#f3f4f6', color: '#6b7280', label: status || '—' };
};

const isClosedTrade = (status) => ['accepted', 'closed', 'completed', 'funded'].includes(String(status || '').toLowerCase());

function TradesList({ trades, loading, error, onRetry, onViewPool }) {
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ height: 72, background: '#f3f4f6', borderRadius: 8, animation: 'uw-fade-in 0.2s ease-out' }} />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ background: '#ffe6e9', border: '1px solid #ffd6db', color: '#ff082f', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
        {error}{' '}
        <button onClick={onRetry} style={{ marginLeft: 8, background: 'none', border: '1px solid #ffd6db', color: '#ff082f', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Retry</button>
      </div>
    );
  }
  if (!trades || trades.length === 0) {
    return <div style={{ fontSize: 12, color: '#9ca3af', padding: 8 }}>No prior trades on file.</div>;
  }
  const closedTrades = trades.filter((t) => isClosedTrade(t.bid_status));
  const closedVolume = closedTrades.reduce((s, t) => s + (Number(t.bid_amount) || 0), 0);
  const fmt$ = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {trades.map((t) => {
        const badge = TRADE_STATUS_BADGE(t.bid_status);
        const poolLabel = t.pool_name || t.pool_number || `Pool #${t.pool_id || '—'}`;
        return (
          <div key={t.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {poolLabel}
                  {t.pool_number && t.pool_name && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: '#9ca3af' }}>· {t.pool_number}</span>}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {(t.loans_in_pool ?? 0)} loan{(t.loans_in_pool === 1) ? '' : 's'} · UPB: {fmt$(t.total_upb)}
                  <span style={{ marginLeft: 10, color: '#3a6ad9', fontWeight: 700 }}>Bid: {fmt$(t.bid_amount)}</span>
                  {t.bid_price_pct && <span style={{ marginLeft: 6, color: '#9ca3af' }}>({Number(t.bid_price_pct).toFixed(2)}%)</span>}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  Submitted: {fmtDate(t.bid_date)}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 700, background: badge.bg, color: badge.color }}>
                  {badge.label}
                </span>
                {t.pool_id && (
                  <button onClick={() => onViewPool(t.pool_id)} type="button"
                    style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    View Pool →
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {closedTrades.length > 0 && (
        <div style={{ marginTop: 6, padding: '10px 12px', background: '#f0fde4', border: '1px solid #d4f9a0', borderRadius: 8, fontSize: 12, color: '#111111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Total Closed Volume</span>
          <span style={{ fontWeight: 700 }}>
            {fmt$(closedVolume)} <span style={{ color: '#374151', fontWeight: 500 }}>across {closedTrades.length} pool{closedTrades.length === 1 ? '' : 's'}</span>
          </span>
        </div>
      )}
    </div>
  );
}
